// ─── IVC level ────────────────────────────────────────────────────────────────
/**
 * NIV.2: co-falla IPRA + IASH (IPRA < 65 AND IASH < 60)
 * NIV.1: falla parcial (IPRA < 65 OR IASH < 60)
 * 0:     Sin IVC
 */
export const computeIvcLevel = (ipra, iash) => {
    const i = parseFloat(ipra);
    const a = parseFloat(iash);
    if (isNaN(i) || isNaN(a)) return 0;
    if (i < 65 && a < 60) return 2;              // Nivel 2 (RIESGO ALTO)
    if (i < 70 || a < 65)  return 1;              // Nivel 1 (RIESGO MODERADO) V4 §3.3
    return 0;
};

// ─── Tone / palette ───────────────────────────────────────────────────────────
export const KPI_TONE_STYLES = {
    critical: { background: '#F3202A', badgeBackground: '#FDE6E8', badgeColor: '#F3202A' },
    high:     { background: '#F48732', badgeBackground: '#FEF0E6', badgeColor: '#F48732' },
    moderate: { background: '#D4AC0D', badgeBackground: '#FFF7D9', badgeColor: '#A87A00' },
    low:      { background: '#62C190', badgeBackground: '#E8F8F0', badgeColor: '#1E7A4E' },
    positive: { background: '#62C190', badgeBackground: '#E8F8F0', badgeColor: '#1E7A4E' },
    neutral:  { background: '#6E88A6', badgeBackground: '#E8EEF5', badgeColor: '#5A6E87' },
};

// ─── Semaphore legend rows ─────────────────────────────────────────────────────
export const SUMMARY_SEMAPHORE_ROWS = [
    { color: '#62C190', ipra: '85 - 100',  iash: '80 - 100',  risk: 'Riesgo Bajo' },
    { color: '#D4AC0D', ipra: '75 - 84.9', iash: '65 - 79.9', risk: 'Riesgo Moderado' },
    { color: '#F48732', ipra: '65 - 74.9', iash: '50 - 64.9', risk: 'Riesgo Alto' },
    { color: '#F3202A', ipra: '0 - 64.9',  iash: '0 - 49.9',  risk: 'Riesgo Crítico' },
];

// ─── Formatting helpers ────────────────────────────────────────────────────────
export const capitalizeFirst = (value = '') =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

export const formatRounded = (value, options = {}) => {
    const { signed = false } = options;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '-';
    const rounded = Math.round(numeric);
    if (signed && rounded > 0) return `+${rounded}`;
    return `${rounded}`;
};

export const formatPercent = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '-';
    const rounded = Math.round(numeric * 10) / 10;
    return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
};

// ─── Data helpers ──────────────────────────────────────────────────────────────
export const getCoveragePercent = (totals = {}) => {
    const total    = Number(totals?.relationships_total);
    const finished = Number(totals?.relationships_finished);
    if (!Number.isFinite(total) || !Number.isFinite(finished) || total <= 0) return null;
    return (finished / total) * 100;
};

export const countBelowThreshold = (rows = [], threshold = 0) =>
    rows.filter((r) => typeof r.average === 'number' && r.average < threshold).length;

export const getCondensedEvaluationLabel = (firstTitle, secondTitle) => {
    const first  = (firstTitle  || '').trim();
    const second = (secondTitle || '').trim();
    if (!first)  return second;
    if (!second) return first;

    const fp = first.split(' - ').map((p) => p.trim()).filter(Boolean);
    const sp = second.split(' - ').map((p) => p.trim()).filter(Boolean);

    if (fp.length > 1 && sp.length > 1 && fp[0] === sp[0]) {
        const tail1 = fp.slice(1).join(' - ');
        const tail2 = sp.slice(1).join(' - ');
        if (tail1 && tail2) return `${fp[0]} · ${tail1} + ${tail2}`;
    }
    return `${first} + ${second}`;
};

export const toDimensionRows = (averageByDimension = {}) =>
    Object.entries(averageByDimension || {})
        .map(([key, dim]) => ({
            key,
            dimension:     dim?.title || key,
            average:       typeof dim?.average       === 'number' ? dim.average       : null,
            weightPercent: typeof dim?.weight_percent === 'number' ? dim.weight_percent : null,
        }))
        .sort((a, b) => (b.average ?? -1) - (a.average ?? -1));

// ─── Risk classification ───────────────────────────────────────────────────────
export const getStatusTone = (value, type = 'icp') => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 'neutral';
    if (type === 'nps')  return n >= 0 ? 'positive' : 'critical';
    if (type === 'iash') return n >= 80 ? 'low' : n >= 65 ? 'moderate' : n >= 50 ? 'high' : 'critical';
    if (type === 'ipra') return n >= 85 ? 'low' : n >= 75 ? 'moderate' : n >= 65 ? 'high' : 'critical';
    return n >= 85 ? 'low' : n >= 75 ? 'moderate' : n >= 65 ? 'high' : 'critical';
};

export const getRiskLabel = (value, type = 'icp') => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 'Sin dato';
    if (type === 'iash') return n >= 80 ? 'Riesgo Bajo' : n >= 65 ? 'Riesgo Moderado' : n >= 50 ? 'Riesgo Alto' : 'Riesgo Crítico';
    if (type === 'ipra') return n >= 85 ? 'Riesgo Bajo' : n >= 75 ? 'Riesgo Moderado' : n >= 65 ? 'Riesgo Alto' : 'Riesgo Crítico';
    return n >= 85 ? 'Riesgo Bajo' : n >= 75 ? 'Riesgo Moderado' : n >= 65 ? 'Riesgo Alto' : 'Riesgo Crítico';
};

// ─── Date helpers ──────────────────────────────────────────────────────────────
/**
 * Returns the Date object for the measurement start, preferring iash start_date,
 * then ipra start_date, then falling back to reportCreatedAt.
 */
export const getMeasurementDate = (evaluations = {}, reportCreatedAt) => {
    const raw = evaluations?.iash?.start_date || evaluations?.ipra?.start_date;
    return raw ? new Date(raw) : new Date(reportCreatedAt || Date.now());
};

export const getQuarterLabel = (date) =>
    `Q${Math.floor(date.getMonth() / 3) + 1} - ${date.getFullYear()}`;

export const getMonthYearLabel = (date) => {
    const formatted = new Intl.DateTimeFormat('es-CO', { month: 'long', year: 'numeric' }).format(date);
    return capitalizeFirst(formatted);
};

/**
 * Returns the number of unique supervisors evaluated in IPRA for the current run.
 * Reads `payload.total_supervisor_count` set by the backend cross-report view.
 * Falls back to 0 if the field is absent or non-numeric.
 */
export const getIpraEvaluatedCount = (payload = {}) => {
    const n = Number(payload?.total_supervisor_count);
    return Number.isFinite(n) && n >= 0 ? n : 0;
};

/**
 * Returns the top N IPRA dimensions sorted by weight_percent DESC.
 * Each entry: { key, title, shortTitle, firstWord, weightPercent }
 */
export const getTopIpraDimensions = (ipraTotals, n = 5) => {
    const avgByDim = (ipraTotals || {}).average_by_dimension || {};
    return Object.entries(avgByDim)
        .map(([key, dim]) => {
            const title = dim?.title || key;
            const baseTitle = title.split('/')[0].trim();
            return {
                key,
                title,
                shortTitle: baseTitle,
                firstWord: baseTitle.split(' ')[0],
                weightPercent: typeof dim?.weight_percent === 'number' ? dim.weight_percent : 0,
            };
        })
        .sort((a, b) => b.weightPercent - a.weightPercent)
        .slice(0, n);
};

// ─── Section cover helpers ─────────────────────────────────────────────────────

/**
 * Extrae las primeras 2 palabras con contenido (≥ 3 chars) del título de dimensión,
 * ignorando el código D{N} inicial y conectores cortos.
 * Ej: "D1 Bienestar Físico y Escucha Corporal" → "Bienestar Físico"
 *     "D5 Disciplina y Hábitos Seguros"        → "Disciplina Hábitos"
 */
const _dimShortInsightLabel = (title) => {
    if (!title) return '';
    const raw     = title.split('/')[0].trim();
    const words   = raw.split(/\s+/).filter(Boolean);
    const codeIdx = words.findIndex(w => /^D\d+$/i.test(w));
    const after   = words.slice(codeIdx >= 0 ? codeIdx + 1 : 0).filter(w => w.length >= 3);
    return after.slice(0, 2).join(' ') || words[0] || '';
};

/**
 * Genera el texto de insight para la sub-portada de sección a partir de los totales.
 * Identifica las 2 dimensiones con mayor impacto de déficit (gap × peso) y calcula
 * qué porcentaje del déficit total concentran.
 *
 * @param {object} totals       – objeto `iash_totals` o `ipra_totals` con `average_by_dimension`
 * @param {number} threshold    – umbral de riesgo (75 para IPRA, 65 para IASH)
 * @param {string} deficitLabel – texto final, ej. "del déficit de liderazgo"
 * @returns {string|null}
 */
export const buildSectionCoverInsight = (totals, threshold, deficitLabel) => {
    const avgByDim = (totals || {}).average_by_dimension || {};
    const rows = Object.entries(avgByDim)
        .map(([key, dim]) => {
            const score        = typeof dim?.average        === 'number' ? dim.average        : null;
            const weightPercent = typeof dim?.weight_percent === 'number' ? dim.weight_percent : 0;
            if (score === null) return null;
            const impact = score < threshold ? (threshold - score) * (weightPercent / 100) : 0;
            return { key, title: dim?.title || key, score, weightPercent, impact };
        })
        .filter(Boolean);

    const totalImpact = rows.reduce((sum, r) => sum + r.impact, 0);
    if (totalImpact === 0 || rows.length < 2) return null;

    const sorted = [...rows].sort((a, b) => b.impact - a.impact);
    const d1 = sorted[0];
    const d2 = sorted[1];
    if (!d1 || d1.impact === 0) return null;

    const top2Impact = d1.impact + (d2?.impact ?? 0);
    const concPct    = Math.round((top2Impact / totalImpact) * 100);
    const l1         = _dimShortInsightLabel(d1.title);
    const l2         = d2 ? _dimShortInsightLabel(d2.title) : null;
    if (!l1) return null;

    const dimPart = l2 ? `${l1} y ${l2}` : l1;
    return `${dimPart} concentran el ${concPct}% ${deficitLabel}.`;
};

/**
 * Returns the number of unique employees evaluated in IASH for the current run.
 * Reads `payload.total_employee_count` set by the backend cross-report view.
 */
export const getIashEvaluatedCount = (payload = {}) => {
    const n = Number(payload?.total_employee_count);
    return Number.isFinite(n) && n >= 0 ? n : 0;
};
